import React from "react";
import { HorizontalStack, VerticalStack, Text, Box, Link, Icon, Button, InlineCode } from "@shopify/polaris";
import { ExternalSmallMinor, TickMinor } from "@shopify/polaris-icons";

import { useMyShopifyDomainSelector } from "@vendor-app/app/_state/reducers/shopify";
import { typedFrontendVendorApi } from "@vendor-app/utils/typedFrontendVendorApi";
import { useToast } from "@vendor-app/app/_sharedComponents/Toast/ToastProvider";

export const GettingStarted = ({ setReloIntegrationStepsComplete }) => {
  const { addToast } = useToast();
  const myShopifyDomain = useMyShopifyDomainSelector();

  const [firstStepDone, setFirstStepDone] = React.useState(false);
  const [secondStepDone, setSecondStepDone] = React.useState(false);

  const klaviyoSetupEmail = () => {
    const myShopifySubdomain = myShopifyDomain?.replace(".myshopify.com", "");
    return `cs+${myShopifySubdomain}@reloapp.co`;
  };

  const postMerchantEnabledIntegration = async () => {
    try {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      const response = await typedFrontendVendorApi.postReq("/relo-integration/merchant-enabled-integration");
      if (response.type === "success") {setReloIntegrationStepsComplete(true);}
    } catch (error) {
      console.error("Error fetching Relo predicted opportunities data:", error);
    }
  };

  const markStepsComplete = () => {
    setSecondStepDone(true);
    postMerchantEnabledIntegration();
    addToast("Relo integration started! Our onboarding team has been notified and will reach out shortly.");
  };

  const StepNumber = ({ number }) => (
    <Box borderColor="border-subdued" borderWidth="1" borderRadius="full" padding="1" width="26px">
      <Text variant="bodySm" as="span" fontWeight="bold" alignment="center">
        {number}
      </Text>
    </Box>
  );

  return (
    <VerticalStack gap="2" align="start">
      <Text variant="headingMd" as="h6">
        Getting Started
      </Text>

      <Box background="bg" borderColor="border-subdued" borderWidth="1" borderRadius="200" padding="4">
        <HorizontalStack gap="2" align="start" blockAlign="start">
          <StepNumber number="1" />
          <VerticalStack gap="2">
            {firstStepDone ? (
              <HorizontalStack gap="1" align="start" blockAlign="center">
                <Text variant="headingMd" as="p">
                  Grant Klaviyo access
                </Text>
                <Icon source={TickMinor} color="success" />
              </HorizontalStack>
            ) : (
              <React.Fragment>
                <HorizontalStack gap="4" align="start" blockAlign="center">
                  <Text variant="headingMd" as="p">
                    Grant Klaviyo access
                  </Text>
                  <HorizontalStack gap="1" blockAlign="center">
                    <Icon source={ExternalSmallMinor} />
                    <Link
                      monochrome={true}
                      url="https://blueprintstore.notion.site/How-to-grant-Relo-temporary-access-to-your-Klaviyo-account-dbad47e7e88c417c88a1674af1750f4c"
                      target="_blank"
                    >
                      Instructions
                    </Link>
                  </HorizontalStack>
                </HorizontalStack>
                <Text variant="bodyMd" as="p" color="subdued" breakWord>
                  The Relo team requires access to your Klaviyo account to create new flows for Relo&apos;s
                  automations.
                </Text>
                <Text variant="bodyMd" as="p" color="subdued" breakWord>
                  Please create a new user in your Klaviyo account with the following:
                </Text>
                <Text variant="bodyMd" as="p">
                  <Text as="span" fontWeight="semibold">
                    Email:
                  </Text>
                  &nbsp;
                  <Text as="span">
                    <InlineCode>{klaviyoSetupEmail()}</InlineCode>
                  </Text>
                </Text>
                <Text variant="bodyMd" as="p">
                  <Text as="span" fontWeight="semibold">
                    Role:
                  </Text>
                  &nbsp;
                  <Text as="span">Manager</Text>
                </Text>
                <Box>
                  <Button primary disabled={firstStepDone} size="medium" onClick={() => setFirstStepDone(true)}>
                    Mark as done
                  </Button>
                </Box>
              </React.Fragment>
            )}
          </VerticalStack>
        </HorizontalStack>
      </Box>

      <Box
        background={firstStepDone ? "bg" : "bg-subdued"}
        borderColor="border-subdued"
        borderWidth="1"
        borderRadius="200"
        padding="4"
      >
        <HorizontalStack gap="2" align="start" blockAlign="start">
          <StepNumber number="2" />
          <VerticalStack gap="2">
            <HorizontalStack gap="1" align="start" blockAlign="center">
              <Text variant="headingMd" as="p" color={firstStepDone ? undefined : "subdued"}>
                Ready to start
              </Text>
              {!!secondStepDone && <Icon source={TickMinor} color="success" />}
            </HorizontalStack>
            {!!firstStepDone && !secondStepDone && (
              <Box>
                <Button primary size="medium" onClick={() => markStepsComplete()}>
                  Start using Relo
                </Button>
              </Box>
            )}
          </VerticalStack>
        </HorizontalStack>
      </Box>
    </VerticalStack>
  );
};
